<template>
  <div>
    <p class="subtitle-2 mb-1" style="color: rgba(0,0,0,0.7)">Tipo de realizado</p>
    <p class="caption grey--text mb-4">Como será medido o desempenho do participante</p>

    <v-item-group v-model="form.tipoRealizado" mandatory>
      <v-row>
        <v-col v-for="opt in tiposRealizado" :key="opt.id" cols="4">
          <v-item :value="opt.id" v-slot="{ active, toggle }">
            <v-card
              outlined
              :color="active ? 'primary' : ''"
              :dark="active"
              @click="toggle"
              style="cursor: pointer"
            >
              <v-card-text class="text-center py-3">
                <v-icon :color="active ? 'white' : 'grey'" class="mb-1">{{ opt.icon }}</v-icon>
                <p class="caption ma-0 font-weight-medium">{{ opt.label }}</p>
              </v-card-text>
            </v-card>
          </v-item>
        </v-col>
      </v-row>
    </v-item-group>

    <v-alert
      v-if="form.tipoRealizado"
      type="info"
      dense
      text
      class="mt-4 caption"
      :class="form.tipoRealizado === 2 ? 'mb-3' : 'mb-0'"
    >{{ descricaoTipoRealizado }}</v-alert>

    <!-- Seleção de Equipes -->
    <div v-if="form.tipoRealizado === 2" class="mt-2">
      <v-text-field
        v-model="filtroEquipe"
        placeholder="Filtrar equipes..."
        outlined
        dense
        prepend-inner-icon="mdi-magnify"
        hide-details
        class="mb-3"
      ></v-text-field>

      <div v-if="loadingEquipes" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
      </div>

      <div v-else style="max-height: 260px; overflow-y: auto; padding-right: 4px">
        <div
          v-if="!filtroEquipe"
          class="d-flex align-center pa-2 mb-1 rounded"
          style="cursor: pointer; border: 1px solid rgba(0,0,0,0.08)"
          :style="
            isEquipeSelecionada({ id_equipe: 1 })
              ? 'border-color: #1976d2; background: rgba(25,118,210,0.05)'
              : 'background: white'
          "
          @click="toggleEquipe({ id_equipe: 1, nome_equipe: 'Equipe de cada participante' })"
        >
          <v-checkbox
            :input-value="isEquipeSelecionada({ id_equipe: 1 })"
            hide-details
            dense
            class="mt-0 pt-0 mr-2 flex-shrink-0"
            @click.stop="toggleEquipe({ id_equipe: 1, nome_equipe: 'Equipe de cada participante' })"
          ></v-checkbox>
          <div class="flex-grow-1">
            <p class="body-2 font-weight-medium ma-0">Equipe de cada participante</p>
            <p class="caption grey--text ma-0">Cada participante considera o realizado da sua própria equipe</p>
          </div>
        </div>

        <div
          v-for="equipe in equipesFiltradas"
          :key="equipe.id_equipe"
          class="d-flex align-center pa-2 mb-1 rounded"
          style="cursor: pointer; border: 1px solid rgba(0,0,0,0.08)"
          :style="
            isEquipeSelecionada(equipe)
              ? 'border-color: #1976d2; background: rgba(25,118,210,0.05)'
              : 'background: white'
          "
          @click="toggleEquipe(equipe)"
        >
          <v-checkbox
            :input-value="isEquipeSelecionada(equipe)"
            hide-details
            dense
            class="mt-0 pt-0 mr-2 flex-shrink-0"
            @click.stop="toggleEquipe(equipe)"
          ></v-checkbox>
          <div class="flex-grow-1">
            <p class="body-2 font-weight-medium ma-0">{{ equipe.nome_equipe }}</p>
            <p class="caption grey--text ma-0">ID {{ equipe.id_equipe }}</p>
          </div>
        </div>

        <p
          v-if="equipesFiltradas.length === 0 && filtroEquipe"
          class="caption grey--text text-center py-4 ma-0"
        >Nenhuma equipe encontrada</p>
      </div>

      <div
        v-if="form.equipesSelecionadas.length > 0"
        class="mt-3 pa-3 rounded"
        style="background: rgba(25,118,210,0.06); border: 1px solid rgba(25,118,210,0.2)"
      >
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center">
            <v-icon small color="primary" class="mr-1">mdi-check</v-icon>
            <span class="body-2 font-weight-medium primary--text">
              {{ form.equipesSelecionadas.length }} selecionada(s)
            </span>
          </div>
          <v-btn x-small text color="primary" @click="form.equipesSelecionadas = []">Limpar</v-btn>
        </div>
        <div class="d-flex flex-wrap" style="gap: 4px">
          <v-chip
            v-for="eq in form.equipesSelecionadas"
            :key="eq.id_equipe"
            x-small
            color="primary"
            outlined
            close
            @click:close="toggleEquipe(eq)"
          >{{ eq.nome_equipe }}</v-chip>
        </div>
      </div>

      <div
        v-if="form.equipesSelecionadas.length === 0"
        class="mt-3 pa-3 d-flex align-start rounded"
        style="background: #fffde7; border: 1px solid #f9a825"
      >
        <v-icon small color="amber darken-2" class="mr-2">mdi-alert-circle</v-icon>
        <span class="caption" style="color: #f57f17">Selecione pelo menos uma opção para continuar.</span>
      </div>

      <v-alert
        v-else-if="form.equipesSelecionadas.length > 1"
        type="info"
        dense
        text
        class="mt-3 caption mb-0"
      >
        Será considerada a <strong>soma</strong> do realizado de todas as equipes selecionadas.
      </v-alert>
    </div>

    <!-- Seleção de Empresas -->
    <div v-if="form.tipoRealizado === 3" class="mt-2">
      <v-text-field
        v-model="filtroEmpresa"
        placeholder="Filtrar empresas..."
        outlined
        dense
        prepend-inner-icon="mdi-magnify"
        hide-details
        class="mb-3"
      ></v-text-field>

      <div v-if="loadingEmpresas" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
      </div>

      <div v-else style="max-height: 260px; overflow-y: auto; padding-right: 4px">
        <div
          v-if="!filtroEmpresa"
          class="d-flex align-center pa-2 mb-1 rounded"
          style="cursor: pointer; border: 1px solid rgba(0,0,0,0.08)"
          :style="
            isEmpresaRealizadoSelecionada({ id_empresa: 1 })
              ? 'border-color: #1976d2; background: rgba(25,118,210,0.05)'
              : 'background: white'
          "
          @click="toggleEmpresaRealizado({ id_empresa: 1, apelido: 'Empresa de cada participante' })"
        >
          <v-checkbox
            :input-value="isEmpresaRealizadoSelecionada({ id_empresa: 1 })"
            hide-details
            dense
            class="mt-0 pt-0 mr-2 flex-shrink-0"
            @click.stop="toggleEmpresaRealizado({ id_empresa: 1, apelido: 'Empresa de cada participante' })"
          ></v-checkbox>
          <div class="flex-grow-1">
            <p class="body-2 font-weight-medium ma-0">Empresa de cada participante</p>
            <p class="caption grey--text ma-0">Cada participante considera o realizado da sua própria empresa</p>
          </div>
        </div>

        <div
          v-for="empresa in empresasParticipantesFiltradas"
          :key="empresa.id_empresa"
          class="d-flex align-center pa-2 mb-1 rounded"
          style="cursor: pointer; border: 1px solid rgba(0,0,0,0.08)"
          :style="
            isEmpresaRealizadoSelecionada(empresa)
              ? 'border-color: #1976d2; background: rgba(25,118,210,0.05)'
              : 'background: white'
          "
          @click="toggleEmpresaRealizado(empresa)"
        >
          <v-checkbox
            :input-value="isEmpresaRealizadoSelecionada(empresa)"
            hide-details
            dense
            class="mt-0 pt-0 mr-2 flex-shrink-0"
            @click.stop="toggleEmpresaRealizado(empresa)"
          ></v-checkbox>
          <div class="flex-grow-1">
            <p class="body-2 font-weight-medium ma-0">{{ empresa.apelido || empresa.nome_empresa }}</p>
            <p class="caption grey--text ma-0">ID {{ empresa.id_empresa }}</p>
          </div>
        </div>

        <p
          v-if="empresasParticipantesFiltradas.length === 0 && filtroEmpresa"
          class="caption grey--text text-center py-4 ma-0"
        >Nenhuma empresa encontrada</p>
      </div>

      <div
        v-if="form.empresasRealizadoSelecionadas.length > 0"
        class="mt-3 pa-3 rounded"
        style="background: rgba(25,118,210,0.06); border: 1px solid rgba(25,118,210,0.2)"
      >
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center">
            <v-icon small color="primary" class="mr-1">mdi-check</v-icon>
            <span class="body-2 font-weight-medium primary--text">
              {{ form.empresasRealizadoSelecionadas.length }} selecionada(s)
            </span>
          </div>
          <v-btn x-small text color="primary" @click="form.empresasRealizadoSelecionadas = []">Limpar</v-btn>
        </div>
        <div class="d-flex flex-wrap" style="gap: 4px">
          <v-chip
            v-for="emp in form.empresasRealizadoSelecionadas"
            :key="emp.id_empresa"
            x-small
            color="primary"
            outlined
            close
            @click:close="toggleEmpresaRealizado(emp)"
          >{{ emp.apelido || emp.nome_empresa }}</v-chip>
        </div>
      </div>

      <div
        v-if="form.empresasRealizadoSelecionadas.length === 0"
        class="mt-3 pa-3 d-flex align-start rounded"
        style="background: #fffde7; border: 1px solid #f9a825"
      >
        <v-icon small color="amber darken-2" class="mr-2">mdi-alert-circle</v-icon>
        <span class="caption" style="color: #f57f17">Selecione pelo menos uma opção para continuar.</span>
      </div>

      <v-alert
        v-else-if="form.empresasRealizadoSelecionadas.length > 1"
        type="info"
        dense
        text
        class="mt-3 caption mb-0"
      >
        Será considerada a <strong>soma</strong> do realizado de todas as empresas selecionadas.
      </v-alert>
    </div>
  </div>
</template>

<script>
export default {
  name: "ModalCadastroSubEtapaRealizado",

  props: {
    form: { type: Object, required: true },
    equipes: { type: Array, default: () => [] },
    empresas: { type: Array, default: () => [] },
    loadingEquipes: { type: Boolean, default: false },
    loadingEmpresas: { type: Boolean, default: false }
  },

  data() {
    return {
      filtroEquipe: "",
      filtroEmpresa: ""
    };
  },

  computed: {
    tiposRealizado() {
      return [
        { id: 1, label: "Individual", icon: "mdi-account" },
        { id: 2, label: "Equipe", icon: "mdi-account-group" },
        { id: 3, label: "Empresa", icon: "mdi-domain" }
      ];
    },

    descricaoTipoRealizado() {
      const map = {
        1: "Os participantes receberão bônus com base no realizado do próprio CPF.",
        2: "Os participantes receberão bônus com base no realizado das equipes selecionadas.",
        3: "Os participantes receberão bônus com base no realizado da(s) empresa(s) selecionada(s)."
      };
      return map[this.form.tipoRealizado] || "";
    },

    equipesFiltradas() {
      if (!this.filtroEquipe) return this.equipes;
      const termo = this.filtroEquipe.toLowerCase();
      return this.equipes.filter(
        e => e.nome_equipe && e.nome_equipe.toLowerCase().includes(termo)
      );
    },

    empresasParticipantesFiltradas() {
      if (!this.filtroEmpresa) return this.empresas;
      const termo = this.filtroEmpresa.toLowerCase();
      return this.empresas.filter(
        e =>
          (e.apelido && e.apelido.toLowerCase().includes(termo)) ||
          (e.nome_empresa && e.nome_empresa.toLowerCase().includes(termo))
      );
    }
  },

  methods: {
    isEquipeSelecionada(equipe) {
      return this.form.equipesSelecionadas.some(e => e.id_equipe === equipe.id_equipe);
    },

    toggleEquipe(equipe) {
      if (this.isEquipeSelecionada(equipe)) {
        this.form.equipesSelecionadas = this.form.equipesSelecionadas.filter(
          e => e.id_equipe !== equipe.id_equipe
        );
      } else {
        this.form.equipesSelecionadas.push(equipe);
      }
    },

    isEmpresaRealizadoSelecionada(empresa) {
      return this.form.empresasRealizadoSelecionadas.some(
        e => e.id_empresa === empresa.id_empresa
      );
    },

    toggleEmpresaRealizado(empresa) {
      if (this.isEmpresaRealizadoSelecionada(empresa)) {
        this.form.empresasRealizadoSelecionadas = this.form.empresasRealizadoSelecionadas.filter(
          e => e.id_empresa !== empresa.id_empresa
        );
      } else {
        this.form.empresasRealizadoSelecionadas.push(empresa);
      }
    }
  }
};
</script>
